/**
 * Minha conta › Notificações no padrão do TiFlux (spec 037, PR B): duas
 * colunas, **Notificações** (o sino) e **E-mails**, e cada evento em duas
 * linhas, "(geral)" e "nos meus tickets". A regra de cada célula é a do
 * @erp/shared (notification-prefs.ts) — a mesma do sino e do funil de e-mail.
 *
 * - "Geral" inclui "nos meus": com o geral ligado, a linha de meus fica marcada
 *   e travada.
 * - E-mail: a primeira mudança na coluna fixa o que a pessoa recebia
 *   (`materializeEmailPrefs`) — quem tinha o e-mail de antes não perde nada ao
 *   mexer numa célula só.
 * - O apontamento e o chat não vão por e-mail: a célula é "—".
 */
import {
  CHAT_MESSAGE_NOTIFICATION_KEY,
  isEmailEventKind,
  isNotificationOn,
  isSingleScopeKind,
  materializeEmailPrefs,
  NOTIFICATION_EVENT_KINDS,
  NOTIFICATION_KIND_LABELS,
  notificationPrefKey,
  type NotificationChannel,
  type NotificationEventKind,
  type NotificationPrefs,
  type NotificationScope,
} from '@erp/shared';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { isPrefEnabled } from './notifications';

/** a mudança de UMA célula, já com o resto das preferências */
export function withCell(
  prefs: NotificationPrefs,
  channel: NotificationChannel,
  scope: NotificationScope,
  kind: NotificationEventKind,
  on: boolean
): NotificationPrefs {
  const base = channel === 'email' ? materializeEmailPrefs(prefs) : { ...prefs };
  // a interna sem chave própria segue a pública: ao mexer na pública, a interna
  // fica como a pessoa a via — senão a linha de baixo mudaria junto, sem clique
  if (channel === 'bell' && kind === 'comment_added' && prefs.internal_comment_added === undefined) {
    base.internal_comment_added = isNotificationOn(prefs, 'bell', 'all', 'internal_comment_added');
  }
  base[notificationPrefKey(channel, scope, kind)] = on;
  return base;
}

function Cell({
  label,
  checked,
  disabled,
  title,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled?: boolean;
  title?: string;
  onChange: (on: boolean) => void;
}) {
  return (
    <span className="flex w-24 justify-center" title={title}>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} aria-label={label} />
    </span>
  );
}

const Dash = () => (
  <span className="flex w-24 justify-center text-muted-foreground/60" aria-label="não vai por e-mail">
    —
  </span>
);

export function NotificationPrefsTable({
  prefs,
  onSave,
}: {
  prefs: NotificationPrefs;
  onSave: (next: NotificationPrefs) => void;
}) {
  const row = (kind: NotificationEventKind, scope: NotificationScope) => {
    const labels = NOTIFICATION_KIND_LABELS[kind];
    // a menção é uma linha só: ela só chega a quem foi mencionado
    const label = isSingleScopeKind(kind) ? labels.all : scope === 'all' ? `${labels.all} (geral)` : labels.mine;
    const bellAll = isNotificationOn(prefs, 'bell', 'all', kind);
    const emailAll = isNotificationOn(prefs, 'email', 'all', kind);
    const included = 'Incluído no geral';
    return (
      <div
        key={`${kind}:${scope}`}
        className={cn(
          'grid h-10 grid-cols-[minmax(0,1fr)_auto_auto] items-center',
          scope === 'all' ? 'border-t border-divider' : ''
        )}
      >
        <span className={cn('truncate text-sm', scope === 'mine' && 'pl-4 text-muted-foreground')}>{label}</span>
        <Cell
          label={`${label} — notificação`}
          checked={isNotificationOn(prefs, 'bell', scope, kind)}
          disabled={scope === 'mine' && bellAll}
          title={scope === 'mine' && bellAll ? included : undefined}
          onChange={(on) => onSave(withCell(prefs, 'bell', scope, kind, on))}
        />
        {isEmailEventKind(kind) ? (
          <Cell
            label={`${label} — e-mail`}
            checked={isNotificationOn(prefs, 'email', scope, kind)}
            disabled={scope === 'mine' && emailAll}
            title={scope === 'mine' && emailAll ? included : undefined}
            onChange={(on) => onSave(withCell(prefs, 'email', scope, kind, on))}
          />
        ) : (
          <Dash />
        )}
      </div>
    );
  };

  return (
    <div className="max-w-2xl" data-tour="conta-notificacoes-tabela">
      <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center pb-2">
        <span className="text-[13px] text-muted-foreground">Evento</span>
        <span className="w-24 text-center text-[13px] font-medium text-muted-foreground">Notificações</span>
        <span className="w-24 text-center text-[13px] font-medium text-muted-foreground" data-tour="conta-notificacoes-emails">
          E-mails
        </span>
      </div>
      {NOTIFICATION_EVENT_KINDS.flatMap((kind) =>
        isSingleScopeKind(kind) ? [row(kind, 'all')] : [row(kind, 'all'), row(kind, 'mine')]
      )}
      <div className="grid h-10 grid-cols-[minmax(0,1fr)_auto_auto] items-center border-t border-divider">
        <span className="truncate text-sm">Mensagens de chat</span>
        <Cell
          label="Mensagens de chat — notificação"
          checked={isPrefEnabled(prefs, CHAT_MESSAGE_NOTIFICATION_KEY)}
          onChange={(on) => onSave({ ...prefs, [CHAT_MESSAGE_NOTIFICATION_KEY]: on })}
        />
        <Dash />
      </div>
    </div>
  );
}
